<script lang="ts">
  import { onMount } from "svelte";
  import { isClient, patchWithJwt, postWithJwt, serverlessRoutes, translate, errorTracker } from "lib";
  import { alertStore, journalMealsStore, mealPlanStore, dialogData, user } from "stores";
  import { ButtonComponent, CheckboxComponent, InputField, TextareaField } from "ui";
  import type { Meal, PartialItemMap } from "interfaces";

  let data: any;

  const form = {
    name: {
      value: "",
      error: ""
    },
    description: {
      value: "",
      error: ""
    },
    showMacros: {
      value: false,
      error: ""
    }
  };

  let disabled = true;
  let isLoading = false;
  const dialogType = data.meal ? translate("EDIT") : translate("CREATE");

  const onInput = (field: keyof typeof form): void => {
    if (field === "name") {
      const { value } = form[field];

      if (value.length === 0) {
        form[field].error = translate("FIELD_REQUIRED");
      } else if (value.length < 2) {
        form[field].error = translate("FIELD_MINIMUM_2");
      } else if (value.length > 320) {
        form[field].error = translate("FIELD_MAXIMUM_320");
      } else {
        form[field].error = "";
      }
    }

    if (field === "description") {
      const { value } = form[field];

      if (value.length > 5000) {
        form[field].error = translate("FIELD_MAXIMUM_5000");
      } else {
        form[field].error = "";
      }
    }

    disabled = Object.values(form).some(({error}): boolean => error !== "");
  }

  const createMeal = async (): Promise<void> => {
    const {mealPlanId} = data;

    const partialMeal: Partial<Meal> = {
      name: form.name.value,
      description: form.description.value,
      showMacros: form.showMacros.value ? 1 : 0,
      mealPlanId,
      position: $mealPlanStore?.meals?.length || 0
    };

    const response = await postWithJwt(serverlessRoutes.MEAL, {partialMeal});

    if (response.error && !response.data) {
      errorTracker.captureError(response.error as Error | string, "error", {
        context: "diet",
        action: "create_recipe_meal",
        mealName: form.name.value
      });
      alertStore.show(translate("ERROR_EDITING_MEAL"), "error");
      isLoading = false;
      return;
    }

    if (isClient($user)) {

    } else {
      mealPlanStore.addMeals([response.data.meal]);
    }

    $dialogData.data = {};
    $dialogData.type = "";
  };

  const editMeal = async (): Promise<void> => {
    const partialMealMap: PartialItemMap<Meal> = {
      [data.meal.id]: {
        name: form.name.value,
        description: form.description.value,
        showMacros: form.showMacros.value ? 1 : 0
      }
    };

    const response = await patchWithJwt(
      serverlessRoutes.MEAL,
      {partialMealMap}
    );

    if (response.error && !response.data) {
      errorTracker.captureError(response.error as Error | string, "error", {
        context: "diet",
        action: "edit_recipe_meal",
        mealId: data.meal.id,
        mealName: form.name.value
      });
      alertStore.show(translate("ERROR_EDITING_MEAL"), "error");
      isLoading = false;
      return;
    }

    if (isClient($user)) {
      journalMealsStore.replace(response.data.meals);
    } else {
      if (data.meal.finishedAt) {
        journalMealsStore.replace(response.data.meals);
      } else {
        mealPlanStore.replaceMeals(response.data.meals);
      }
    }

    $dialogData.data = {};
    $dialogData.type = "";
  };

  const onSubmit = async (): Promise<void> => {
    isLoading = true;

    if (data.meal) {
      await editMeal();
    } else {
      await createMeal();
    }
  };

  onMount((): void => {
    if (data.meal) {
      const { name, description, showMacros } = data.meal;

      form.name.value = name || "";
      form.description.value = description || "";
      form.showMacros.value = showMacros;

      onInput("name");
      onInput("description");
    }
  });

  export { data };
</script>

<div class="p-4 flex flex-col gap-4">
  <div class="text-center font-semibold">
    {dialogType}
    {translate("MEAL").toLowerCase()}
  </div>

  <form class="flex flex-col gap-4" on:submit|preventDefault={onSubmit}>
    <InputField
      label="{translate("NAME")}"
      error="{form.name.error}"
      bind:value="{form.name.value}"
      on:input="{() => onInput("name")}"/>

    <TextareaField
      label="{translate("DESCRIPTION")}"
      error="{form.description.error}"
      bind:value="{form.description.value}"
      on:input="{() => onInput("description")}"/>

    <CheckboxComponent
      label="{translate("DISPLAY_MACRONUTRIENT_VALUES")}"
      bind:value={form.showMacros.value}/>

    <div class="flex justify-center">
      <ButtonComponent {disabled} {isLoading} type="submit">
        {dialogType}
      </ButtonComponent>
    </div>
  </form>
</div>
